import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import { DollarBillIcon } from "@/components/DollarBillIcon";

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [readyToReset, setReadyToReset] = useState(false);

  // Extract token/type from URL (query or hash)
  const token = searchParams.get("token");
  const type = searchParams.get("type");

  useEffect(() => {
    // Check if we have a valid reset session
    // Supabase password reset links put tokens in the URL hash (e.g. #access_token=...&type=recovery)
    const checkSession = async () => {
      // Check URL hash for Supabase tokens
      const hashParams = new URLSearchParams(window.location.hash?.substring(1) || "");
      const hashType = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");

      // If we have tokens in hash, set the session
      if (hashType === "recovery" && accessToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || "",
        });

        if (sessionError) {
          console.error("Error setting reset session:", sessionError);
          toast({
            title: "Invalid Reset Link",
            description: "This password reset link is invalid or has expired. Please request a new one.",
            variant: "destructive",
          });
          navigate("/auth", { replace: true });
          return;
        }

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);
      }

      // Check if we have a valid session (from hash or from query token)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (token || type) {
          toast({
            title: "Invalid Reset Link",
            description: "This password reset link is invalid or has expired. Please request a new one.",
            variant: "destructive",
          });
        }
        navigate("/auth", { replace: true });
        return;
      }

      setReadyToReset(true);
    };

    checkSession();
  }, [token, type, navigate, toast]);

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      // Verify we have a session (Supabase sets this when user clicks reset link)
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        // No session means the token might have expired or is invalid
        toast({
          title: "Session Expired",
          description: "This password reset link has expired or is invalid. Please request a new password reset link.",
          variant: "destructive",
        });
        setLoading(false);
        navigate("/auth", { replace: true });
        return;
      }

      // Update password using the session
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Your password has been reset successfully. You can now sign in with your new password.",
      });

      // Sign out the user so they can sign in with new password
      await supabase.auth.signOut();

      // Redirect to auth page after a short delay
      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 2000);
    } catch (error) {
      console.error("Password reset error:", error);
      toast({
        title: "Error",
        description: error instanceof Error 
          ? error.message 
          : "Failed to reset password. The link may have expired. Please request a new password reset.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show form when we have a valid session (set from hash or already present); otherwise redirect happens in useEffect
  if (!readyToReset) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <DollarBillIcon size={64} />
          <h1 className="text-3xl font-bold"><span className="text-foreground">Dollar</span>{" "}<span className="text-vault-green">Bill</span></h1>
        </div>

        <Card className="shadow-elegant">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-bold text-center">Reset Password</CardTitle>
            <CardDescription className="text-center">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    required
                    className="pr-10"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    className="pr-10"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Resetting Password..." : "Reset Password"}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/auth")}
                className="text-sm text-primary hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
